import { Injectable } from '@nestjs/common';
import { PrismaService } from '@cart/api-data-access';
import { ProductSummary, WishlistItemSummary } from '@cart/shared-types';
import { StoreContextService } from '../tenancy/store-context.service';
import { PRODUCTS } from '../products/products.data';

export abstract class WishlistRepository {
  abstract list(userId: string): Promise<WishlistItemSummary[]>;
  abstract findActiveProduct(productId: string): Promise<ProductSummary | undefined>;
  abstract exists(userId: string, productId: string): Promise<boolean>;
  abstract add(userId: string, product: ProductSummary): Promise<WishlistItemSummary>;
  abstract delete(userId: string, itemId: string): Promise<boolean>;
}

@Injectable()
export class PrismaWishlistRepository implements WishlistRepository {
  constructor(
    private readonly prisma: PrismaService,
    private readonly storeContext?: StoreContextService
  ) {}

  async list(userId: string): Promise<WishlistItemSummary[]> {
    const items = await this.prisma.wishlistItem.findMany({
      where: { storeId: this.storeId(), userId },
      include: this.productInclude(),
      orderBy: { createdAt: 'desc' },
    });

    return items.map((item) => ({
      id: item.id,
      product: this.toProductSummary(item.product),
      createdAt: item.createdAt.toISOString(),
    }));
  }

  async findActiveProduct(productId: string): Promise<ProductSummary | undefined> {
    const product = await this.prisma.product.findFirst({
      where: { id: productId, storeId: this.storeId(), active: true },
      include: {
        category: { select: { slug: true } },
        images: { orderBy: { sortOrder: 'asc' }, select: { url: true }, take: 1 },
        inventory: { select: { quantity: true } },
      },
    });

    return product ? this.toProductSummary(product) : undefined;
  }

  async exists(userId: string, productId: string): Promise<boolean> {
    const item = await this.prisma.wishlistItem.findFirst({
      where: { storeId: this.storeId(), userId, productId },
      select: { id: true },
    });

    return Boolean(item);
  }

  async add(userId: string, product: ProductSummary): Promise<WishlistItemSummary> {
    const item = await this.prisma.wishlistItem.create({
      data: { storeId: this.storeId(), userId, productId: product.id },
    });

    return {
      id: item.id,
      product,
      createdAt: item.createdAt.toISOString(),
    };
  }

  async delete(userId: string, itemId: string): Promise<boolean> {
    const item = await this.prisma.wishlistItem.findFirst({
      where: { id: itemId, storeId: this.storeId(), userId },
      select: { id: true },
    });

    if (!item) {
      return false;
    }

    await this.prisma.wishlistItem.delete({ where: { id: itemId } });

    return true;
  }

  private productInclude() {
    return {
      product: {
        include: {
          category: { select: { slug: true } },
          images: { orderBy: { sortOrder: 'asc' as const }, select: { url: true }, take: 1 },
          inventory: { select: { quantity: true } },
        },
      },
    };
  }

  private toProductSummary(product: {
    id: string;
    name: string;
    slug: string;
    price: number;
    active: boolean;
    createdAt: Date;
    category: { slug: string };
    images: { url: string }[];
    inventory: { quantity: number } | null;
  }): ProductSummary {
    return {
      id: product.id,
      name: product.name,
      slug: product.slug,
      category: product.category.slug,
      price: product.price,
      imageUrl: product.images[0]?.url ?? '',
      mediaSizeBytes: 0,
      stock: product.inventory?.quantity ?? 0,
      active: product.active,
      createdAt: product.createdAt.toISOString(),
    };
  }

  private storeId(): string {
    return this.storeContext?.storeId() ?? 'store_demo';
  }
}

@Injectable()
export class InMemoryWishlistRepository implements WishlistRepository {
  private readonly items: Array<WishlistItemSummary & { userId: string }> = [];

  async list(userId: string): Promise<WishlistItemSummary[]> {
    return this.items
      .filter((item) => item.userId === userId)
      .map((item) => this.toPublicItem(item));
  }

  async findActiveProduct(productId: string): Promise<ProductSummary | undefined> {
    return PRODUCTS.find((product) => product.id === productId && product.active);
  }

  async exists(userId: string, productId: string): Promise<boolean> {
    return this.items.some((item) => item.userId === userId && item.product.id === productId);
  }

  async add(userId: string, product: ProductSummary): Promise<WishlistItemSummary> {
    const item = {
      id: `wish-${String(this.items.length + 1).padStart(3, '0')}`,
      userId,
      product,
      createdAt: new Date().toISOString(),
    };

    this.items.push(item);

    return this.toPublicItem(item);
  }

  async delete(userId: string, itemId: string): Promise<boolean> {
    const index = this.items.findIndex((item) => item.userId === userId && item.id === itemId);

    if (index === -1) {
      return false;
    }

    this.items.splice(index, 1);

    return true;
  }

  private toPublicItem(item: WishlistItemSummary & { userId: string }): WishlistItemSummary {
    return {
      id: item.id,
      product: item.product,
      createdAt: item.createdAt,
    };
  }
}
